import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Location { id: number; name: string; }
interface Inventory {
  id: number; name: string; sku: string; description: string;
  jenis: 'bahan_baku' | 'produk_jadi';
  stock: number; purchase_price: number; selling_price: number;
  location_id: number; location_name: string;
  image: string | null;
}

const IMG_BASE = (process.env.REACT_APP_IMG_URL || 'http://localhost:3000/uploads') + '/';

type Jenis = 'bahan_baku' | 'produk_jadi';

const emptyForm = {
  name: '', sku: '', description: '',
  jenis: 'produk_jadi' as Jenis,
  stock: 0, purchase_price: 0, selling_price: 0, location_id: '',
};

const generateSKU = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const rand = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `PRD-${rand}`;
};

const rupiahFormat = (n: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

export default function Inventaris() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [items, setItems] = useState<Inventory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterJenis, setFilterJenis] = useState<'semua' | Jenis>('semua');
  const [filterLokasi, setFilterLokasi] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Inventory | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [removeImage, setRemoveImage] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const [invRes, locRes] = await Promise.allSettled([
      api.get('/inventories/getAllInventories'),
      api.get('/locations/getAllLocations'),
    ]);
    if (invRes.status === 'fulfilled') setItems(invRes.value.data);
    if (locRes.status === 'fulfilled') setLocations(locRes.value.data);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openAdd = () => {
    setEditItem(null);
    setForm({ ...emptyForm, sku: generateSKU() });
    setImageFile(null);
    setImagePreview('');
    setRemoveImage(false);
    setError('');
    setShowModal(true);
  };

  const openEdit = (item: Inventory) => {
    setEditItem(item);
    setForm({
      name: item.name, sku: item.sku, description: item.description || '',
      jenis: item.jenis || 'produk_jadi',
      stock: item.stock, purchase_price: item.purchase_price,
      selling_price: item.selling_price,
      location_id: item.location_id?.toString() || '',
    });
    setImageFile(null);
    setImagePreview(item.image ? `${IMG_BASE}${item.image}` : '');
    setRemoveImage(false);
    setError('');
    setShowModal(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setRemoveImage(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('sku', form.sku);
      fd.append('description', form.description);
      fd.append('jenis', form.jenis);
      fd.append('stock', String(form.stock));
      fd.append('purchase_price', String(form.purchase_price));
      fd.append('selling_price', String(form.selling_price));
      fd.append('location_id', form.location_id ? String(form.location_id) : '');
      if (imageFile) fd.append('image', imageFile);
      if (removeImage) fd.append('remove_image', '1');

      if (editItem) {
        await api.put(`/inventories/updateInventory/${editItem.id}`, fd);
      } else {
        await api.post('/inventories/createInventory', fd);
      }
      setShowModal(false);
      setImageFile(null);
      setImagePreview('');
      setRemoveImage(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/inventories/deleteInventory/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch {
      setError('Gagal menghapus');
    }
  };

  const filtered = items.filter((i) => {
    const matchSearch = i.name?.toLowerCase().includes(search.toLowerCase()) || i.sku?.toLowerCase().includes(search.toLowerCase());
    const matchJenis = filterJenis === 'semua' || i.jenis === filterJenis;
    const matchLokasi = !filterLokasi || String(i.location_id) === filterLokasi;
    return matchSearch && matchJenis && matchLokasi;
  });

  const countBB = items.filter((i) => i.jenis === 'bahan_baku').length;
  const countPJ = items.filter((i) => i.jenis === 'produk_jadi').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Inventaris</h1>
          <p className="text-gray-500 text-sm mt-1">
            <span className="text-orange-500 font-medium">{countBB} bahan baku</span>
            {' · '}
            <span className="text-indigo-500 font-medium">{countPJ} produk jadi</span>
          </p>
        </div>
        {isAdmin && (
          <button onClick={openAdd}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Tambah Produk
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

      {/* Filter + Search */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Filter jenis */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
            {([['semua', 'Semua'], ['bahan_baku', 'Bahan Baku'], ['produk_jadi', 'Produk Jadi']] as const).map(([val, label]) => (
              <button key={val} onClick={() => setFilterJenis(val)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${filterJenis === val ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Filter lokasi */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <select value={filterLokasi} onChange={(e) => setFilterLokasi(e.target.value)}
              className="pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white appearance-none cursor-pointer">
              <option value="">Semua Lokasi</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Cari nama atau SKU..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
        </div>

        {/* Active filter indicator */}
        {(filterLokasi || filterJenis !== 'semua' || search) && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400">Filter aktif:</span>
            {filterJenis !== 'semua' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                {filterJenis === 'bahan_baku' ? 'Bahan Baku' : 'Produk Jadi'}
                <button onClick={() => setFilterJenis('semua')} className="ml-0.5 hover:text-indigo-900">×</button>
              </span>
            )}
            {filterLokasi && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                {locations.find((l) => String(l.id) === filterLokasi)?.name}
                <button onClick={() => setFilterLokasi('')} className="ml-0.5 hover:text-green-900">×</button>
              </span>
            )}
            {search && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                "{search}"
                <button onClick={() => setSearch('')} className="ml-0.5 hover:text-gray-900">×</button>
              </span>
            )}
            <button onClick={() => { setFilterJenis('semua'); setFilterLokasi(''); setSearch(''); }}
              className="text-xs text-red-500 hover:text-red-700 font-medium">
              Reset semua
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-400">
            <svg className="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Memuat...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Produk</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Jenis</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">SKU</th>
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Stok</th>
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Harga Beli</th>
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Harga Jual</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Lokasi</th>
                  {isAdmin && <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Aksi</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 8 : 7} className="text-center py-12 text-gray-400">
                      {search ? 'Produk tidak ditemukan' : 'Belum ada produk'}
                    </td>
                  </tr>
                ) : filtered.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-800">{item.name}</div>
                      {item.description && <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{item.description}</div>}
                    </td>
                    <td className="px-5 py-4">
                      {item.jenis === 'bahan_baku' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
                          Bahan Baku
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                          Produk Jadi
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-gray-500 font-mono text-xs">{item.sku || '-'}</td>
                    <td className="px-5 py-4 text-right">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${item.stock <= 5 ? 'bg-red-100 text-red-700' : item.stock <= 20 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                        {item.stock}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right text-gray-500 text-xs">
                      {item.purchase_price ? rupiahFormat(item.purchase_price) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-4 text-right font-medium text-gray-800">
                      {item.jenis === 'produk_jadi' && item.selling_price ? rupiahFormat(item.selling_price) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-4 text-gray-500">{item.location_name || '-'}</td>
                    {isAdmin && (
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => openEdit(item)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition" title="Edit">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => setDeleteId(item.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition" title="Hapus">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">{editItem ? 'Edit Produk' : 'Tambah Produk'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

              {/* Toggle Jenis */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Jenis Produk *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setForm({ ...form, jenis: 'bahan_baku' })}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition ${form.jenis === 'bahan_baku' ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
                    <div className="text-left">
                      <p className="font-semibold">Bahan Baku</p>
                      <p className="text-xs font-normal opacity-70">Dibeli, lalu diproses</p>
                    </div>
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, jenis: 'produk_jadi' })}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition ${form.jenis === 'produk_jadi' ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                    <div className="text-left">
                      <p className="font-semibold">Produk Jadi</p>
                      <p className="text-xs font-normal opacity-70">Siap dijual ke customer</p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nama *</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={form.jenis === 'bahan_baku' ? 'cth: Kacang Tanah Curah' : 'cth: Kacang Bungkus 100gr'} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">SKU</label>
                  <div className="flex gap-2">
                    <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
                    {!editItem && (
                      <button type="button" onClick={() => setForm({ ...form, sku: generateSKU() })} title="Generate ulang"
                        className="px-2.5 border border-gray-200 rounded-xl text-gray-400 hover:text-indigo-600 hover:border-indigo-300 transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Stok Awal</label>
                  <input type="number" min={0} value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  <p className="text-xs text-gray-400 mt-1">Biarkan 0 jika belum ada stok</p>
                </div>

                {/* Harga Beli — tampil untuk bahan baku, opsional untuk produk jadi */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Harga Beli (Rp)
                    {form.jenis === 'bahan_baku' && <span className="text-gray-400 font-normal ml-1">— referensi</span>}
                  </label>
                  <input type="number" min={0} value={form.purchase_price}
                    onChange={(e) => setForm({ ...form, purchase_price: Number(e.target.value) })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="0" />
                </div>

                {/* Harga Jual — hanya untuk produk jadi */}
                {form.jenis === 'produk_jadi' ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Harga Jual (Rp) *</label>
                    <input required type="number" min={0} value={form.selling_price}
                      onChange={(e) => setForm({ ...form, selling_price: Number(e.target.value) })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                ) : (
                  <div className="flex items-end">
                    <div className="w-full bg-orange-50 border border-orange-100 rounded-xl px-3 py-2.5 text-xs text-orange-600">
                      💡 Bahan baku tidak perlu harga jual. Harga jual ada di produk jadinya.
                    </div>
                  </div>
                )}

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lokasi</label>
                  <select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">-- Pilih Lokasi --</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Deskripsi</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    placeholder="Deskripsi (opsional)" />
                </div>

                {/* Upload Gambar */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Foto Produk
                    <span className="text-gray-400 font-normal ml-1">— JPG/PNG/WebP, maks 2MB</span>
                  </label>
                  {imagePreview ? (
                    <div className="relative w-full h-40 rounded-xl overflow-hidden border border-gray-200 group">
                      <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                        <label className="cursor-pointer bg-white text-gray-700 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-50 transition">
                          Ganti Foto
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageChange} />
                        </label>
                        <button type="button" onClick={handleRemoveImage}
                          className="bg-red-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-red-600 transition">
                          Hapus
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition">
                      <svg className="w-8 h-8 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs text-gray-400">Klik untuk upload foto</span>
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageChange} />
                    </label>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Batal</button>
                <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition">
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Hapus Produk?</h3>
            <p className="text-gray-500 text-sm mb-6">Tindakan ini tidak bisa dibatalkan.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Batal</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 transition">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
